import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { supabase } from '../supabaseClient';
import { getPendingInvite, clearPendingInvite } from '../onboarding/inviteStorage';

type Mode = 'signin' | 'signup' | 'reset';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export default function Login() {
  // Si llegó por un enlace de invitación, o desde el CTA de alta de la landing
  // (?signup), arrancamos en modo registro.
  const pendingInvite = getPendingInvite();
  const wantsSignup = new URLSearchParams(window.location.search).has('signup');
  const [mode, setMode] = useState<Mode>(pendingInvite || wantsSignup ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Distingue el mensaje de la pantalla de confirmación (alta vs recuperación).
  const [sentKind, setSentKind] = useState<'signup' | 'reset'>('signup');

  const signInWithGoogle = async () => {
    setError('');
    setBusy(true);
    // No limpiamos el invite pendiente: tras volver de Google, el onboarding
    // lo recupera (getPendingInvite) y une al equipo por el enlace.
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/bandeja` },
    });
    if (err) { setError(err.message); setBusy(false); }
    // Si no hay error, el navegador redirige a Google (no reseteamos busy).
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'signin') {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password: pass });
        if (err) throw err;
      } else if (mode === 'reset') {
        const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/nueva-password`,
        });
        if (err) throw err;
        setSentKind('reset');
        setSentTo(email);
      } else {
        const { error: err } = await supabase.auth.signUp({
          email,
          password: pass,
          options: {
            emailRedirectTo: `${window.location.origin}/bandeja`,
            data: {
              nombre: nombre || email.split('@')[0],
              // El color ya no se elige en el alta: handle_new_user autoasigna un
              // color libre de la paleta dentro del equipo. Se cambia luego en el perfil.
              // Si vino por enlace de invitación, handle_new_user lo mete en ese equipo.
              ...(pendingInvite
                ? { join_team_id: pendingInvite.teamId, join_token: pendingInvite.token }
                : {}),
            },
          },
        });
        if (err) throw err;
        clearPendingInvite();
        setSentKind('signup');
        setSentTo(email);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al autenticar';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <div style={styles.screen}>
        <div style={styles.card}>
          <h1 style={styles.title}>Revisa tu email</h1>
          <p style={styles.subtitle}>
            {sentKind === 'reset' ? (
              <>Te hemos enviado un enlace a <strong>{sentTo}</strong> para restablecer tu contraseña. Ábrelo desde el mismo dispositivo y elige una nueva.</>
            ) : (
              <>Te hemos enviado un enlace de confirmación a <strong>{sentTo}</strong>. Ábrelo desde el mismo dispositivo para activar tu cuenta y entrar en SimplyTeam.</>
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              setSentTo(null);
              setMode('signin');
              setEmail('');
              setPass('');
              setNombre('');
              setError('');
            }}
            style={styles.button}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <form onSubmit={handleSubmit} style={styles.card}>
        <div style={styles.brandRow}>
          <img src={`${process.env.PUBLIC_URL}/simplyteam-mark.svg`} alt="" width={36} height={36} />
          <h1 style={styles.title}>Simply<span style={{ color: '#2563eb' }}>Team</span></h1>
        </div>
        {pendingInvite ? (
          <p style={styles.subtitle}>
            Te han invitado a unirte a <strong>{pendingInvite.teamName}</strong>. Crea tu cuenta para entrar.
          </p>
        ) : (
          <p style={styles.subtitle}>
            {mode === 'signin' ? 'Accede a tu bandeja'
              : mode === 'reset' ? 'Te enviaremos un enlace para crear una nueva contraseña'
              : 'Crea tu cuenta'}
          </p>
        )}

        {mode !== 'reset' && (
          <>
            <button type="button" onClick={signInWithGoogle} disabled={busy} style={styles.googleBtn}>
              <GoogleIcon />
              Continuar con Google
            </button>
            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>o</span>
              <span style={styles.dividerLine} />
            </div>
          </>
        )}

        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          style={styles.input}
        />
        {mode !== 'reset' && (
          <input
            type="password"
            placeholder="Contraseña"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            required
            style={styles.input}
          />
        )}

        {mode === 'signup' && (
          <>
            <input
              type="text"
              placeholder="Apodo o nombre corto (p. ej. Carlos, Jorge.rrhh)"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={60}
              style={styles.input}
            />
            <span style={styles.fieldHint}>
              Usa un apodo o nombre corto por el que te conozcan tus compañeros. Tu color te lo asignamos automáticamente y podrás cambiarlo luego en tu perfil.
            </span>
          </>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button type="submit" disabled={busy} style={styles.button}>
          {busy ? '...' : mode === 'signin' ? 'Acceder' : mode === 'reset' ? 'Enviar enlace' : 'Crear cuenta'}
        </button>

        {mode === 'signin' && !pendingInvite && (
          <button
            type="button"
            onClick={() => { setMode('reset'); setError(''); }}
            style={styles.linkBtn}
          >
            ¿Olvidaste tu contraseña?
          </button>
        )}

        <button
          type="button"
          onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(''); }}
          style={styles.linkBtn}
        >
          {mode === 'signin' ? '¿No tienes cuenta? Crear una' : 'Volver a acceder'}
        </button>

        <div style={styles.legal}>
          <a href="https://simplyteam.app/aviso-legal" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Aviso legal</a>
          <span style={styles.legalSep}>·</span>
          <a href="https://simplyteam.app/privacidad" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Privacidad</a>
          <span style={styles.legalSep}>·</span>
          <a href="https://simplyteam.app/cookies" target="_blank" rel="noopener noreferrer" style={styles.legalLink}>Cookies</a>
        </div>
      </form>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  screen: {
    minHeight: '100vh',
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    padding: 24,
  },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '40px 36px',
    width: 380,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    textAlign: 'center',
    margin: 0,
    color: '#0f172a',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: 14,
    margin: '0 0 8px',
  },
  input: {
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '11px 14px',
    color: '#0f172a',
    fontSize: 15,
    outline: 'none',
  },
  fieldHint: {
    fontSize: 12.5,
    color: '#64748b',
    lineHeight: 1.4,
    margin: '-6px 2px 2px',
  },
  error: {
    color: '#dc2626',
    fontSize: 13,
    textAlign: 'center',
    margin: 0,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '11px 0',
    fontSize: 15,
    fontWeight: 600,
    color: '#0f172a',
    cursor: 'pointer',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '2px 0',
  },
  dividerLine: { flex: 1, height: 1, background: '#e2e8f0' },
  dividerText: { fontSize: 12, color: '#94a3b8', fontWeight: 600 },
  button: {
    background: '#16a34a',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '11px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: 13,
    cursor: 'pointer',
    padding: 4,
  },
  legal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
    paddingTop: 14,
    borderTop: '1px solid #f1f5f9',
  },
  legalLink: { color: '#94a3b8', fontSize: 12, textDecoration: 'none' },
  legalSep: { color: '#cbd5e1', fontSize: 12 },
};
